import { ACQUISITION_SOURCES, GAME_CODE_REGEX, VERTICALS } from '@scoreboard/shared';
import type { AcquisitionSource, Vertical } from '@scoreboard/shared';

// Boucle virale, côté webapp animateur (doc 06 §7.3, dernière maille du jalon J8).
//
// La vitrine pose `?src=`, `?d=` et `?vertical=` sur ses CTA vers `/app/games/new`
// (`apps/website/src/lib/links.ts`). Jusqu'ici la webapp les ignorait : la partie
// n'était pas pré-typée et le `signup` n'était rattaché à aucune origine.
//
// MÊME MÉMOIRE QUE LA VITRINE, pas un tracking parallèle. Mono-domaine (arbitrage 21
// du doc 00) : `scorofun.com/go` et `scorofun.com/app/…` partagent la même origine,
// donc le même `localStorage`. On relit donc la clé `sf.attr` écrite par
// `apps/website/src/components/Acquisition.astro`, au même format et avec la même
// règle de first-touch 30 jours — quelqu'un qui a scanné l'écran d'une soirée la
// semaine dernière reste attribué à cette soirée même s'il revient par Google.
//
// Aucune donnée personnelle : trois champs de flux, aucun identifiant, aucun cookie.

const KEY = 'sf.attr';
const MAX_AGE_MS = 30 * 86_400_000;

export interface Attribution {
  src?: AcquisitionSource;
  /** Code court de la partie d'origine (`?d=XK4F`). */
  d?: string;
  vertical?: Vertical;
}

interface StoredAttribution extends Attribution {
  /** Horodatage du first-touch — c'est lui qui périme l'attribution à 30 jours. */
  ts: number;
}

/**
 * `localStorage` peut manquer ou lever (mode privé, cookies bloqués, WebView
 * verrouillée) : la mesure ne doit JAMAIS empêcher quelqu'un de créer une partie.
 * Sans mémoire, l'attribution est simplement absente.
 */
function storage(): Storage | null {
  try {
    return globalThis.localStorage ?? null;
  } catch {
    return null;
  }
}

const isSource = (v: string | null): v is AcquisitionSource =>
  v !== null && (ACQUISITION_SOURCES as readonly string[]).includes(v);

const isVertical = (v: string | null): v is Vertical =>
  v !== null && (VERTICALS as readonly string[]).includes(v);

const isCode = (v: string | null): v is string => v !== null && GAME_CODE_REGEX.test(v);

/** Ce que porte l'URL courante, une fois le vocabulaire fermé appliqué. */
export function attributionFromSearch(search: string): Attribution {
  const q = new URLSearchParams(search);
  const src = q.get('src');
  const d = q.get('d');
  const vertical = q.get('vertical');
  return {
    ...(isSource(src) ? { src } : {}),
    ...(isCode(d) ? { d } : {}),
    ...(isVertical(vertical) ? { vertical } : {}),
  };
}

/** L'attribution mémorisée, ou `null` si absente, illisible ou périmée. */
export function readAttribution(): Attribution | null {
  const store = storage();
  if (store === null) return null;
  try {
    const raw = store.getItem(KEY);
    if (raw === null) return null;
    const parsed = JSON.parse(raw) as StoredAttribution;
    if (typeof parsed?.ts !== 'number' || Date.now() - parsed.ts >= MAX_AGE_MS) return null;
    const { ts: _ts, ...attribution } = parsed;
    return attribution;
  } catch {
    return null;
  }
}

/**
 * Lit l'URL d'entrée et mémorise l'origine en FIRST-TOUCH : la première gagne et
 * n'est plus écrasée pendant 30 jours. Rend l'attribution effective.
 *
 * Appelée une fois à l'amorçage (`main.tsx`) et pas dans un composant : le parcours
 * réel passe par l'email (`/app/auth?token=…`), qui a perdu les paramètres d'origine.
 * Si on attendait l'écran de création de partie pour les lire, ils n'y seraient plus.
 */
export function captureAttribution(search: string): Attribution | null {
  const incoming = attributionFromSearch(search);
  const stored = readAttribution();
  if (stored !== null || incoming.src === undefined) {
    return stored;
  }
  const store = storage();
  if (store !== null) {
    try {
      store.setItem(KEY, JSON.stringify({ ...incoming, ts: Date.now() } satisfies StoredAttribution));
    } catch {
      /* mode privé : on continue sans mémoire, l'attribution sera simplement absente */
    }
  }
  return incoming;
}

/**
 * Verticale à pré-sélectionner dans le formulaire de création (doc 06 §7.2 : « le
 * CTA crée une partie du même type »).
 *
 * L'URL l'emporte sur la mémoire : la page de la vitrine d'où l'on vient est plus
 * précise que la verticale mémorisée il y a trois semaines. C'est la règle inverse
 * de `src`, qui est du first-touch — les deux champs ne mesurent pas la même chose.
 */
export function preselectedVertical(search: string): Vertical | '' {
  return attributionFromSearch(search).vertical ?? readAttribution()?.vertical ?? '';
}
